<template>
	<div data-tour="cc-dashboard">
		<ClientConnectDashboardList
			:isToDo="true"
			:isDoneForYou="false"
			:isLoading="isLoading"
			:error="error"
			:noAlertsFound="noToDoAlertsFound"
			:statusTotals="statusTotals"
			:dashboardAlerts="toDoAlerts"
			@sendToBlinkClicked="handleBlinkClick"
			@sendEmailClicked="handleSendEmail"
			@alertClicked="handleAlertClick"
			@nameClicked="handleContactClick"
			@addNoteClicked="handleAddNoteClick"
			@markAsClicked="handleUpdateAlertStatus"
		/>
		<ClientConnectDashboardList
			:isToDo="false"
			:isDoneForYou="true"
			:isLoading="isLoading"
			:error="error"
			:noAlertsFound="noDoneForYouAlertsFound"
			:statusTotals="statusTotals"
			:dashboardAlerts="doneForYouAlerts"
			@sendToBlinkClicked="handleBlinkClick"
			@sendEmailClicked="handleSendEmail"
			@alertClicked="handleAlertClick"
			@nameClicked="handleContactClick"
			@addNoteClicked="handleAddNoteClick"
			@markAsClicked="handleUpdateAlertStatus"
		/>
		<OverviewModal
			v-if="showOverviewModal"
			@close="handleOverviewModalClose"
			:leadId="leadIdClicked"
			:alertId="alertIdClicked"
			:modalView="modalView"
			:success="success"
			:waiting="waiting"
			:typeOfNote="'alert'"
			:leadInfo="leadInfo"
			:sendContact="sendContactToBlink"
			:blinkDisabled="isImpersonated"
			:blinkUnregistered="blinkUnregistered"
			:loggedInUserId="loggedInUserId"
			@alertStatusUpdated="updateAlertsTable"
		/>
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { isEmpty } from 'lodash';

import ClientConnectDashboardList from './ClientConnectDashboardList.vue';
import OverviewModal from './modals/OverviewModal.vue';
import { VIEWS } from './modals/constants';
import { STATUS_ACTION_TYPES as STATUSID } from './constants';
import { CAMPAIGN_ID } from '../../constants/brand-builder';
import alertsService from '@/services/contacts/alerts-service.js';

export default {
	name: 'ClientConnectV2Dashboard',
	data() {
		return {
			error: null,
			isLoading: null,
			success: false,
			waiting: false,
			modalView: null,
			showOverviewModal: false,
			alertIdClicked: null,
			VIEWS,
			STATUSID,
			leadInfo: {
				firstName: null,
				lastName: null,
				email: null,
			},
			sendContactToBlink: false,
			blinkUnregistered: false,
		};
	},
	components: {
		ClientConnectDashboardList,
		OverviewModal,
	},
	computed: {
		...mapGetters(['currentUserRolodexIds', 'isImpersonated']),
		...mapState({
			statusTotals: (state) =>
				state.clientConnectV2.alerts.alertStatusTotals,
			toDoAlerts: (state) =>
				state.clientConnectV2.alerts.dashboard.toDoList,
			noToDoAlertsFound: (state) =>
				state.clientConnectV2.alerts.dashboard.noToDoAlertsFound,
			doneForYouAlerts: (state) =>
				state.clientConnectV2.alerts.dashboard.doneForYouList,
			noDoneForYouAlertsFound: (state) =>
				state.clientConnectV2.alerts.dashboard.noDoneForYouAlertsFound,
			types: (state) => state.clientConnectV2.alerts.types.data,
			statuses: (state) => state.clientConnectV2.alerts.statuses.data,
			companyHasBlinkRegistration: (state) =>
				state.companyProfile.blinkRegistrationInd,
			userHasBlinkRegistration: (state) =>
				state.profile.blinkRegistrationInd,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			loggedInUserId: (state) =>
				state.profileSwitcher.mainProfile.rolodexContactId,
		}),
	},
	methods: {
		...mapActions([
			'getAlertStatusTotals',
			'getDashboardAlerts',
			'getAlertTypes',
			'getAlertStatuses',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		async getAlerts() {
			try {
				await Promise.all([
					this.getDashboardAlerts([STATUSID.NEW, STATUSID.TO_DO]),
					this.getDashboardAlerts([STATUSID.DONE_FOR_YOU]),
				]);
			} catch (error) {
				this.error = error;
			}
		},
		handleAddBlinkData(lead) {
			this.leadInfo.firstName = lead.firstName;
			this.leadInfo.lastName = lead.lastName;
			this.leadInfo.email = lead.email;
			this.leadInfo.birthday = lead.birthday;
			if (!this.companyHasBlinkRegistration) {
				this.blinkUnregistered = true;
			} else if (
				this.companyHasBlinkRegistration &&
				!this.userHasBlinkRegistration
			) {
				this.blinkUnregistered = true;
			} else {
				this.blinkUnregistered = false;
			}
			this.sendContactToBlink = true;
		},
		handleBlinkClick(lead) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.showOverviewModal = true;
			this.modalView = VIEWS.SEND_TO_BLINK;
		},
		handleOverviewModalClose() {
			this.showOverviewModal = false;
			this.leadIdClicked = null;
			this.alertIdClicked = null;
			this.success = false;
		},
		async handleAlertClick(lead, alertId, alertStatus) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.alertIdClicked = alertId;
			this.showOverviewModal = true;
			this.modalView = VIEWS.ALERT_DETAILS;

			// This changes the status from new to to-do when the user opens the alert
			if (alertStatus === 'New' && !this.mainProfileIsInternalUser) {
				const payload = {
					alertIds: [alertId],
					statusId: STATUSID.TO_DO,
				};
				await alertsService.updateAlertStatus(payload);
				await this.getAlerts(true);
			}
		},
		handleAddNoteClick(lead, alertId) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.alertIdClicked = alertId;
			this.showOverviewModal = true;
			this.modalView = VIEWS.ADD_NOTE;
		},
		handleContactClick(lead) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.showOverviewModal = true;
			this.modalView = VIEWS.HOME;
		},
		async handleUpdateAlertStatus(statusId, alertId) {
			let payload = {};

			payload = {
				alertIds: [alertId],
				statusId,
			};

			this.isLoading = true;
			await alertsService.updateAlertStatus(payload);
			await this.getAlerts();
			this.isLoading = false;
		},
		async updateAlertsTable() {
			await this.getAlerts();
		},
		async getAlertStatusCount() {
			const { entityId, contactsIds } = this.currentUserRolodexIds;
			const payload = {
				entityId,
				contactsIds,
			};
			await this.getAlertStatusTotals(payload);
		},
		handleSendEmail(alert) {
			const campaignId = CAMPAIGN_ID[alert.type];
			this.$router.push({
				path: `/campaign-editor-workspace/${campaignId}/media`,
				query: {
					alertId: alert.alertId,
					borrowerFirstName: alert.lead.firstName,
					borrowerLastName: alert.lead.lastName,
					email: alert.lead.email,
					leadId: alert.lead.leadId,
					alertType: alert.type,
				},
			});
		},
	},
	async created() {
		this.getAlertStatusCount();
		this.isLoading = true;
		await this.getAlerts();
		this.isLoading = false;
		if (isEmpty(this.statuses)) {
			await this.getAlertStatuses();
		}
		if (isEmpty(this.types)) {
			await this.getAlertTypes();
		}
	},
};
</script>
